import React, { InputHTMLAttributes } from "react";
import styled from "@emotion/styled";
import { compassColors } from "@utils/styling";
import { fontP2Regular } from "src/design-system/styles/fonts";
import { resetFormElement } from "src/design-system/styles/resets";
import Checkbox from "./Checkbox";

const Label = styled.label<{ checked: boolean }>`
  ${fontP2Regular}
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  background-color: ${compassColors.offWhite};
  border: none;
  border-radius: 8px;
  box-sizing: border-box;
  padding: 0 16px;
  min-height: 60px;
  width: 100%;

  ${(props) =>
    props.checked
      ? `
          background-color: ${compassColors.lagoon};
          color: ${compassColors.white};
         `
      : /* Limit hover styles to devices that support hover (i.e. not mobile).
           Fixes a bug where when unselected on mobile the hover state would
           persist and make it look like the button was still selected.
         */
        `
      @media (hover: hover) {
        &:hover {
          background-color: ${compassColors.sand2};
        }
      }`}
`;

const CheckboxInput = styled.input`
  appearance: none;
  border: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

const TextInput = styled.input<{ checked: boolean }>`
  ${resetFormElement}
  background-color: inherit;
  outline: none;
  height: 100%;
  width: 100%;

  ${(props) =>
    props.checked
      ? `
        &::placeholder {
          color: ${compassColors.white};
          opacity: 1;
        }

        &:-ms-input-placeholder {
          /* Internet Explorer 10-11 */
          color: ${compassColors.white};
        }

        &::-ms-input-placeholder {
          /* Microsoft Edge */
          color: ${compassColors.white};
        }
        `
      : `
        &::placeholder {
          color: ${compassColors.black};
          opacity: 1;
        }

        &:-ms-input-placeholder {
          /* Internet Explorer 10-11 */
          color: ${compassColors.black};
        }

        &::-ms-input-placeholder {
          /* Microsoft Edge */
          color: ${compassColors.black};
        }
        `}
`;

type Props = InputHTMLAttributes<HTMLInputElement>;

export default function CheckboxButton({
  checked,
  children,
  ...inputProps
}: Props) {
  return (
    <Label checked={checked}>
      <CheckboxInput
        type="checkbox"
        checked={checked}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...inputProps}
      />
      <Checkbox checked={checked} />
      {children}
    </Label>
  );
}

export function CheckboxTextInput({ checked, ...inputProps }: Props) {
  return (
    <Label checked={checked}>
      <Checkbox checked={checked} />
      <TextInput
        checked={checked}
        type="text"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...inputProps}
      />
    </Label>
  );
}
